import { useMemo } from "react";
import { HardHat, MapPin, ClipboardList } from "lucide-react";
import { SERVICE_COLORS, todayStr, isActiveOn } from "./crewUtils";

export function OnAssignment({ assignments }) {
  const today = todayStr();
  const active = useMemo(
    () => assignments.filter((a) => isActiveOn(a, today) && a.status !== "complete"),
    [assignments, today]
  );

  const byCrew = useMemo(() => {
    const map = new Map();
    for (const a of active) {
      const crew = a.crew?.length ? a.crew : ["Unassigned"];
      for (const name of crew) {
        if (!map.has(name)) map.set(name, []);
        map.get(name).push(a);
      }
    }
    return Array.from(map.entries()).sort((x, y) => x[0].localeCompare(y[0]));
  }, [active]);

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-8" data-testid="on-assignment-panel">
      <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">On Assignment Today</div>
      <p className="mt-1 text-sm text-[#23282B]/60">{today} — {active.length} active job{active.length === 1 ? "" : "s"}, {byCrew.filter(([n]) => n !== "Unassigned").length} crew out.</p>

      {active.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-[#23282B]/20 p-10 text-center text-[#23282B]/60" data-testid="on-assignment-empty">
          <ClipboardList size={30} className="mx-auto text-[#2F5F80]" />
          <div className="mt-2 font-display text-xl uppercase text-[#23282B]">Nobody's booked out today</div>
          <p className="text-sm mt-1">Schedule jobs from the Calendar tab and they'll show up here.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="on-assignment-grid">
          {byCrew.map(([name, jobs]) => (
            <div key={name} className="border-2 border-[#23282B]/10 p-5" data-testid={`crew-card-${name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 inline-flex items-center justify-center bg-[#23282B] text-[#9CC4DB]"><HardHat size={17} /></span>
                <div className="font-display text-xl uppercase text-[#23282B]">{name}</div>
              </div>
              <div className="mt-3 space-y-2">
                {jobs.map((a) => (
                  <div key={a.id} className="border-l-4 pl-3 py-1" style={{ borderColor: SERVICE_COLORS[a.service_type] || "#23282B" }}>
                    <div className="text-sm font-bold text-[#23282B]">{a.title}</div>
                    <div className="text-xs text-[#23282B]/65 flex items-center gap-3 mt-0.5">
                      {a.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {a.location}</span>}
                      <span>{a.start_date}{a.end_date !== a.start_date ? ` → ${a.end_date}` : ""}</span>
                    </div>
                    {a.notes && <div className="text-xs text-[#23282B]/55 mt-0.5">{a.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
